import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import MenuGuideButton from '../components/MenuGuideButton'
import { useToast } from '../ui/ToastProvider'
import {
  EMPTY_CONTRACT,
  Field,
  billingModeLabel,
  displayDateTime,
  effectiveStateLabel,
  statusLabel,
} from './billing/orgBillingUtils'

const ORG_CONTRACT_PAGE_SIZES = [10, 20, 50]

function orgListSortMark(activeKey, direction, key) {
  if (activeKey !== key) return ''
  return direction === 'asc' ? '↑' : '↓'
}

function parseContractInstant(value) {
  if (value == null || value === '') return null
  const t = new Date(value).getTime()
  return Number.isNaN(t) ? null : t
}

export default function BillingOrgContractListPage() {
  const { show } = useToast()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [registerOpen, setRegisterOpen] = useState(false)
  const [orgOptions, setOrgOptions] = useState([])
  const [plans, setPlans] = useState([])
  const [orgContracts, setOrgContracts] = useState([])
  const [contractForm, setContractForm] = useState(EMPTY_CONTRACT)
  const [contractOrgSearch, setContractOrgSearch] = useState('')
  const [contractTargetOrgs, setContractTargetOrgs] = useState([])
  const [includeDescendants, setIncludeDescendants] = useState(true)
  const [targetsLoading, setTargetsLoading] = useState(false)

  const [listSearch, setListSearch] = useState('')
  const [listStatus, setListStatus] = useState('ALL')
  const [listPlan, setListPlan] = useState('ALL')
  const [listEffective, setListEffective] = useState('ALL')
  const [listBillingMode, setListBillingMode] = useState('ALL')
  const [listSortKey, setListSortKey] = useState('orgName')
  const [listSortDirection, setListSortDirection] = useState('asc')
  const [listPage, setListPage] = useState(1)
  const [listPageSize, setListPageSize] = useState(20)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [orgsRes, plansRes, contractsRes] = await Promise.all([
        api.get('/orgs/options'),
        api.get('/billing/admin/config/plans'),
        api.get('/billing/admin/config/org-contracts'),
      ])
      const orgs = (orgsRes?.data?.data ?? orgsRes?.data ?? []).filter((item) => item?.type !== 'PERSONAL')
      setOrgOptions(Array.isArray(orgs) ? orgs : [])
      setPlans(Array.isArray(plansRes?.data?.data) ? plansRes.data.data : [])
      setOrgContracts(Array.isArray(contractsRes?.data?.data) ? contractsRes.data.data : [])
    } catch (e) {
      show('조직 계약 목록을 불러오지 못했습니다.', 'error')
    } finally {
      setLoading(false)
    }
  }, [show])

  useEffect(() => {
    loadData()
  }, [loadData])

  const filteredOrgOptionsForContract = useMemo(() => {
    const q = contractOrgSearch.trim().toLowerCase()
    if (!q) return orgOptions
    return orgOptions.filter(
      (o) =>
        String(o.name || '')
          .toLowerCase()
          .includes(q) || String(o.id || '').includes(q),
    )
  }, [orgOptions, contractOrgSearch])

  useEffect(() => {
    if (!registerOpen || !contractForm.orgId) {
      setContractTargetOrgs([])
      return
    }
    let cancelled = false
    const loadTargets = async () => {
      setTargetsLoading(true)
      try {
        const res = await api.get('/billing/admin/config/org-contract-target-orgs', {
          params: { rootOrgId: Number(contractForm.orgId), includeDescendants },
        })
        const rows = Array.isArray(res?.data?.data) ? res.data.data : []
        if (!cancelled) setContractTargetOrgs(rows)
      } catch (e) {
        if (!cancelled) {
          setContractTargetOrgs([])
          show('적용 대상 조직을 불러오지 못했습니다.', 'error')
        }
      } finally {
        if (!cancelled) setTargetsLoading(false)
      }
    }
    loadTargets()
    return () => {
      cancelled = true
    }
  }, [contractForm.orgId, includeDescendants, registerOpen, show])

  const planFilterOptions = useMemo(() => {
    const fromData = new Set(
      orgContracts.map((c) => (c.appliedPlanCode ? String(c.appliedPlanCode) : '')).filter(Boolean),
    )
    plans.forEach((p) => {
      if (p?.planCode) fromData.add(String(p.planCode))
    })
    return Array.from(fromData).sort((a, b) => a.localeCompare(b, 'ko'))
  }, [orgContracts, plans])

  const filteredContracts = useMemo(() => {
    const q = listSearch.trim().toLowerCase()
    return orgContracts.filter((c) => {
      if (q) {
        const blob = [c.orgName, c.orgId, c.id, c.appliedPlanCode]
          .filter((v) => v != null && v !== '')
          .map((v) => String(v).toLowerCase())
          .join(' ')
        if (!blob.includes(q)) return false
      }
      if (listStatus !== 'ALL' && String(c.status || '') !== listStatus) return false
      if (listPlan !== 'ALL' && String(c.appliedPlanCode || '') !== listPlan) return false
      if (listBillingMode !== 'ALL' && String(c.billingMode || '') !== listBillingMode) return false
      if (listEffective === 'EFFECTIVE' && !c.currentlyEffective) return false
      if (listEffective === 'NOT_EFFECTIVE' && c.currentlyEffective) return false
      return true
    })
  }, [orgContracts, listSearch, listStatus, listPlan, listBillingMode, listEffective])

  const sortedContracts = useMemo(() => {
    const dir = listSortDirection === 'asc' ? 1 : -1
    const cmpStr = (a, b) => String(a || '').localeCompare(String(b || ''), 'ko') * dir
    const cmpNum = (a, b) => (Number(a) || 0) - (Number(b) || 0)
    return [...filteredContracts].sort((a, b) => {
      let r = 0
      switch (listSortKey) {
        case 'orgName':
          r = cmpStr(a.orgName || `조직 #${a.orgId}`, b.orgName || `조직 #${b.orgId}`)
          break
        case 'id':
          r = cmpNum(a.id, b.id) * dir
          break
        case 'billingMode':
          r = cmpStr(a.billingMode, b.billingMode)
          break
        case 'appliedPlanCode':
          r = cmpStr(a.appliedPlanCode, b.appliedPlanCode)
          break
        case 'status':
          r = cmpStr(a.status, b.status)
          if (r === 0) r = cmpStr(a.effectiveStateLabel, b.effectiveStateLabel)
          break
        case 'startedAt': {
          const ta = parseContractInstant(a.startedAt)
          const tb = parseContractInstant(b.startedAt)
          if (ta == null && tb == null) r = 0
          else if (ta == null) r = 1
          else if (tb == null) r = -1
          else r = (ta - tb) * dir
          break
        }
        case 'endsAt': {
          const ta = parseContractInstant(a.endsAt)
          const tb = parseContractInstant(b.endsAt)
          if (ta == null && tb == null) r = 0
          else if (ta == null) r = 1
          else if (tb == null) r = -1
          else r = (ta - tb) * dir
          break
        }
        case 'currentlyEffective':
          r = (Boolean(a.currentlyEffective) === Boolean(b.currentlyEffective) ? 0 : a.currentlyEffective ? 1 : -1) * dir
          break
        default:
          r = 0
      }
      if (r !== 0) return r
      return cmpNum(a.id, b.id)
    })
  }, [filteredContracts, listSortKey, listSortDirection])

  const listTotalPages = Math.max(1, Math.ceil(sortedContracts.length / listPageSize))
  const safeListPage = Math.min(listPage, listTotalPages)

  const pagedContracts = useMemo(() => {
    const start = (safeListPage - 1) * listPageSize
    return sortedContracts.slice(start, start + listPageSize)
  }, [sortedContracts, safeListPage, listPageSize])

  useEffect(() => {
    setListPage(1)
  }, [listSearch, listStatus, listPlan, listEffective, listBillingMode, listSortKey, listSortDirection, listPageSize])

  const changeListSort = (key) => {
    if (listSortKey === key) {
      setListSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setListSortKey(key)
      setListSortDirection(['startedAt', 'endsAt', 'id', 'currentlyEffective'].includes(key) ? 'desc' : 'asc')
    }
  }

  const resetListFilters = () => {
    setListSearch('')
    setListStatus('ALL')
    setListPlan('ALL')
    setListEffective('ALL')
    setListBillingMode('ALL')
    setListSortKey('orgName')
    setListSortDirection('asc')
  }

  const effectiveContractOrgIds = useMemo(() => {
    const ids = orgContracts.filter((c) => c.currentlyEffective).map((c) => Number(c.orgId))
    return new Set(ids)
  }, [orgContracts])

  const registerTargetHasEffectiveConflict =
    registerOpen && contractTargetOrgs.some((o) => effectiveContractOrgIds.has(Number(o.id)))

  const closeRegisterModal = () => {
    setRegisterOpen(false)
    setContractForm(EMPTY_CONTRACT)
    setContractOrgSearch('')
    setContractTargetOrgs([])
    setIncludeDescendants(true)
  }

  const saveNewContract = async () => {
    if (!contractForm.orgId) {
      show('조직을 선택해 주세요.', 'error')
      return
    }
    if (registerTargetHasEffectiveConflict) {
      show('적용 대상에 이미 유효한 조직 계약이 있는 조직이 포함되어 있습니다.', 'error')
      return
    }
    setSaving(true)
    try {
      const payload = {
        orgId: Number(contractForm.orgId),
        billingMode: contractForm.billingMode,
        appliedPlanCode: contractForm.appliedPlanCode,
        seatCountPolicy: contractForm.seatCountPolicy,
        contractedSeats: Number(contractForm.contractedSeats) || 0,
        status: contractForm.status,
        contactEmail: contractForm.contactEmail || null,
        contactPhone: contractForm.contactPhone || null,
        notes: contractForm.notes || null,
        startedAt: contractForm.startedAt || null,
        endsAt: contractForm.endsAt || null,
        graceUntil: contractForm.graceUntil || null,
        includeDescendants,
      }
      const res = await api.post('/billing/admin/config/org-contracts/bulk', payload)
      const n = Number(res?.data?.data?.createdCount) || 1
      show(n > 1 ? `조직 계약이 ${n}개 조직에 등록되었습니다.` : '조직 계약이 등록되었습니다.', 'success')
      closeRegisterModal()
      loadData()
    } catch (e) {
      const errMsg = e?.response?.data?.message || e?.response?.data?.error
      show(errMsg || '조직 계약 저장에 실패했습니다.', 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <section className="py-10 text-center text-slate-500">로딩 중...</section>
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">조직관리</h1>
            <MenuGuideButton slug="subscriptions" title="구독·결제 가이드" anchor="step-02" />
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
          onClick={() => {
            setContractForm(EMPTY_CONTRACT)
            setContractOrgSearch('')
            setRegisterOpen(true)
          }}
        >
          조직 계약 등록
        </button>
      </div>

      <div className="rounded-lg border bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px] flex-1">
            <label className="mb-1 block text-xs font-medium text-slate-700">검색</label>
            <input
              type="search"
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="조직명·조직 ID·계약 ID·플랜 코드"
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
              aria-label="조직 계약 목록 검색"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700">계약 상태</label>
            <select
              className="w-full min-w-[8rem] rounded border border-slate-300 px-3 py-2 text-sm"
              value={listStatus}
              onChange={(e) => setListStatus(e.target.value)}
              aria-label="계약 상태 필터"
            >
              <option value="ALL">전체</option>
              <option value="ACTIVE">활성</option>
              <option value="TERMINATING">종료 예정(유예)</option>
              <option value="INACTIVE">비활성</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700">적용 여부</label>
            <select
              className="w-full min-w-[8rem] rounded border border-slate-300 px-3 py-2 text-sm"
              value={listEffective}
              onChange={(e) => setListEffective(e.target.value)}
              aria-label="적용 여부 필터"
            >
              <option value="ALL">전체</option>
              <option value="EFFECTIVE">현재 적용 중</option>
              <option value="NOT_EFFECTIVE">미적용</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700">플랜</label>
            <select
              className="w-full min-w-[8rem] rounded border border-slate-300 px-3 py-2 text-sm"
              value={listPlan}
              onChange={(e) => setListPlan(e.target.value)}
              aria-label="플랜 필터"
            >
              <option value="ALL">전체</option>
              {planFilterOptions.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-700">과금 모델</label>
            <select
              className="w-full min-w-[10rem] rounded border border-slate-300 px-3 py-2 text-sm"
              value={listBillingMode}
              onChange={(e) => setListBillingMode(e.target.value)}
              aria-label="과금 모델 필터"
            >
              <option value="ALL">전체</option>
              <option value="ORG_PAYS">조직 결제</option>
              <option value="MEMBER_PAYS_WITH_ORG_DISCOUNT">조직 할인 개인 결제</option>
            </select>
          </div>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            onClick={resetListFilters}
          >
            필터 초기화
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          표시 {sortedContracts.length === 0 ? 0 : (safeListPage - 1) * listPageSize + 1}-
          {Math.min(safeListPage * listPageSize, sortedContracts.length)} / 필터 {sortedContracts.length}건 · 전체 {orgContracts.length}건
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <label className="text-xs font-medium text-slate-700" htmlFor="org-contract-page-size">
            페이지당
          </label>
          <select
            id="org-contract-page-size"
            className="rounded border border-slate-300 px-2 py-1 text-xs"
            value={listPageSize}
            onChange={(e) => setListPageSize(Number(e.target.value) || 20)}
            aria-label="조직 계약 목록 페이지 크기"
          >
            {ORG_CONTRACT_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}건
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 font-medium text-slate-700 hover:text-slate-900"
                  onClick={() => changeListSort('orgName')}
                >
                  조직
                  <span className="text-xs font-normal text-slate-400">{orgListSortMark(listSortKey, listSortDirection, 'orgName')}</span>
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 font-medium text-slate-700 hover:text-slate-900"
                  onClick={() => changeListSort('id')}
                >
                  계약
                  <span className="text-xs font-normal text-slate-400">{orgListSortMark(listSortKey, listSortDirection, 'id')}</span>
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 font-medium text-slate-700 hover:text-slate-900"
                  onClick={() => changeListSort('billingMode')}
                >
                  과금
                  <span className="text-xs font-normal text-slate-400">{orgListSortMark(listSortKey, listSortDirection, 'billingMode')}</span>
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 font-medium text-slate-700 hover:text-slate-900"
                  onClick={() => changeListSort('appliedPlanCode')}
                >
                  플랜
                  <span className="text-xs font-normal text-slate-400">{orgListSortMark(listSortKey, listSortDirection, 'appliedPlanCode')}</span>
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 font-medium text-slate-700 hover:text-slate-900"
                  onClick={() => changeListSort('status')}
                >
                  상태
                  <span className="text-xs font-normal text-slate-400">{orgListSortMark(listSortKey, listSortDirection, 'status')}</span>
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-700">기간</span>
                  <button
                    type="button"
                    className="rounded border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-100"
                    onClick={() => changeListSort('startedAt')}
                    title="시작일 기준 정렬"
                  >
                    시작
                    <span className="ml-0.5 text-slate-400">{orgListSortMark(listSortKey, listSortDirection, 'startedAt')}</span>
                  </button>
                  <button
                    type="button"
                    className="rounded border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-100"
                    onClick={() => changeListSort('endsAt')}
                    title="종료일 기준 정렬"
                  >
                    종료
                    <span className="ml-0.5 text-slate-400">{orgListSortMark(listSortKey, listSortDirection, 'endsAt')}</span>
                  </button>
                  <button
                    type="button"
                    className="rounded border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-100"
                    onClick={() => changeListSort('currentlyEffective')}
                    title="적용 여부 정렬"
                  >
                    적용
                    <span className="ml-0.5 text-slate-400">{orgListSortMark(listSortKey, listSortDirection, 'currentlyEffective')}</span>
                  </button>
                </div>
              </th>
              <th className="px-4 py-3 text-left"> </th>
            </tr>
          </thead>
          <tbody>
            {sortedContracts.length === 0 && (
              <tr>
                <td className="px-4 py-8 text-center text-slate-500" colSpan={7}>
                  {orgContracts.length === 0
                    ? '등록된 조직 계약이 없습니다. 상단에서 새 계약을 등록하세요.'
                    : '조건에 맞는 조직 계약이 없습니다. 필터를 조정해 보세요.'}
                </td>
              </tr>
            )}
            {pagedContracts.map((contract) => (
              <tr
                key={contract.id}
                role="button"
                tabIndex={0}
                className="cursor-pointer border-t border-slate-100 hover:bg-slate-50/80"
                onClick={() => navigate(`contracts/${contract.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    navigate(`contracts/${contract.id}`)
                  }
                }}
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{contract.orgName || `조직 #${contract.orgId}`}</div>
                  <div className="text-xs text-slate-500">조직 ID {contract.orgId}</div>
                </td>
                <td className="px-4 py-3">#{contract.id}</td>
                <td className="px-4 py-3">{billingModeLabel(contract.billingMode)}</td>
                <td className="px-4 py-3">{contract.appliedPlanCode || '-'}</td>
                <td className="px-4 py-3">
                  <div>{statusLabel(contract.status)}</div>
                  <div className="text-xs text-slate-500">{effectiveStateLabel(contract)}</div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  <div>시작 {displayDateTime(contract.startedAt)}</div>
                  <div>종료 {displayDateTime(contract.endsAt)}</div>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`contracts/${contract.id}`}
                    className="inline-flex rounded border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                    onClick={(e) => e.stopPropagation()}
                  >
                    상세
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <OrgContractTablePager
          totalCount={sortedContracts.length}
          page={safeListPage}
          pageSize={listPageSize}
          totalPages={listTotalPages}
          onPrev={() => setListPage((p) => Math.max(1, p - 1))}
          onNext={() => setListPage((p) => Math.min(listTotalPages, p + 1))}
        />
      </div>

      {registerOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation" onClick={closeRegisterModal}>
          <div
            className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border bg-white p-6 shadow-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="org-contract-register-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <h2 id="org-contract-register-title" className="text-lg font-semibold text-slate-900">
                조직 계약 등록
              </h2>
              <button type="button" className="rounded border px-2 py-1 text-sm text-slate-600 hover:bg-slate-50" onClick={closeRegisterModal}>
                닫기
              </button>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Field label="조직 검색">
                <input
                  type="search"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={contractOrgSearch}
                  onChange={(e) => setContractOrgSearch(e.target.value)}
                  placeholder="이름 또는 ID로 필터…"
                  aria-label="조직 계약 조직 검색"
                />
              </Field>
              <Field label="기준 조직 선택">
                <select
                  aria-label="조직 계약 대상 조직"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={contractForm.orgId}
                  onChange={(e) => setContractForm((prev) => ({ ...prev, orgId: e.target.value }))}
                >
                  <option value="">조직 선택</option>
                  {filteredOrgOptionsForContract.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name} ({org.type}, #{org.id})
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="적용 범위">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-brand-navy focus:ring-brand-navy"
                    checked={includeDescendants}
                    onChange={(e) => setIncludeDescendants(e.target.checked)}
                  />
                  하위 조직까지 동일 조건으로 생성
                </label>
              </Field>
              {contractForm.orgId ? (
                <div className="md:col-span-3 rounded border border-slate-200 bg-slate-50/80 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-medium text-slate-800">계약이 생성될 조직 ({contractTargetOrgs.length}곳)</p>
                    {targetsLoading ? <span className="text-xs text-slate-500">불러오는 중…</span> : null}
                  </div>
                  {registerTargetHasEffectiveConflict ? (
                    <p className="mt-2 text-sm text-amber-900">
                      표시된 조직 중 일부에 이미 유효한 계약이 있어 이 범위로는 추가 등록할 수 없습니다. 해당 조직의 기존 계약을 종료·비활성 처리한 뒤 다시 시도하세요.
                    </p>
                  ) : null}
                  {!targetsLoading && contractTargetOrgs.length === 0 ? (
                    <p className="mt-2 text-xs text-amber-700">적용 대상을 불러오지 못했습니다. 기준 조직을 다시 선택해 주세요.</p>
                  ) : (
                    <ul className="mt-2 max-h-44 divide-y divide-slate-200 overflow-auto rounded border border-slate-200 bg-white text-sm">
                      {contractTargetOrgs.map((o) => (
                        <li
                          key={o.id}
                          className={`flex items-center justify-between gap-3 px-3 py-2 ${
                            effectiveContractOrgIds.has(Number(o.id)) ? 'bg-amber-50/90' : ''
                          }`}
                        >
                          <span className="text-slate-800">
                            {o.isRoot ? <span className="mr-2 text-xs font-semibold text-brand-navy">기준</span> : null}
                            {o.name}
                            {effectiveContractOrgIds.has(Number(o.id)) ? (
                              <span className="ml-2 text-xs font-medium text-amber-800">유효 계약 있음</span>
                            ) : null}
                          </span>
                          <span className="shrink-0 text-xs text-slate-500">
                            {o.type} · #{o.id}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
              <Field label="과금 모델">
                <select
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={contractForm.billingMode}
                  onChange={(e) => setContractForm((prev) => ({ ...prev, billingMode: e.target.value }))}
                >
                  <option value="ORG_PAYS">조직 결제 (단체 계약)</option>
                </select>
              </Field>
              <Field label="적용 플랜">
                <select
                  aria-label="조직 계약 적용 플랜"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={contractForm.appliedPlanCode}
                  onChange={(e) => setContractForm((prev) => ({ ...prev, appliedPlanCode: e.target.value }))}
                >
                  {plans.map((plan) => (
                    <option key={plan.planCode} value={plan.planCode}>
                      {plan.planCode}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="상태">
                <select
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={contractForm.status}
                  onChange={(e) => setContractForm((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <option value="ACTIVE">활성</option>
                  <option value="TERMINATING">종료 예정(유예)</option>
                  <option value="INACTIVE">비활성</option>
                </select>
              </Field>
              <Field label="좌석 정책">
                <select
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={contractForm.seatCountPolicy}
                  onChange={(e) => setContractForm((prev) => ({ ...prev, seatCountPolicy: e.target.value }))}
                >
                  <option value="APPROVED_MEMBER_COUNT">승인된 멤버 수 기준</option>
                  <option value="CONTRACTED_SEATS">계약 좌석 수 기준</option>
                </select>
              </Field>
              <Field label="계약 좌석 수">
                <input
                  type="number"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={contractForm.contractedSeats}
                  onChange={(e) => setContractForm((prev) => ({ ...prev, contractedSeats: e.target.value }))}
                />
              </Field>
              <Field label="연락 이메일">
                <input
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={contractForm.contactEmail}
                  onChange={(e) => setContractForm((prev) => ({ ...prev, contactEmail: e.target.value }))}
                />
              </Field>
              <Field label="연락 전화번호">
                <input
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={contractForm.contactPhone}
                  onChange={(e) => setContractForm((prev) => ({ ...prev, contactPhone: e.target.value }))}
                />
              </Field>
              <Field label="계약 시작일">
                <input
                  type="datetime-local"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={contractForm.startedAt}
                  onChange={(e) => setContractForm((prev) => ({ ...prev, startedAt: e.target.value }))}
                />
              </Field>
              <Field label="계약 종료일">
                <input
                  type="datetime-local"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={contractForm.endsAt}
                  onChange={(e) => setContractForm((prev) => ({ ...prev, endsAt: e.target.value }))}
                />
              </Field>
              <Field label="유예 종료일">
                <input
                  type="datetime-local"
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={contractForm.graceUntil}
                  onChange={(e) => setContractForm((prev) => ({ ...prev, graceUntil: e.target.value }))}
                />
              </Field>
              <Field label="메모">
                <input
                  className="w-full rounded border px-3 py-2 text-sm"
                  value={contractForm.notes}
                  onChange={(e) => setContractForm((prev) => ({ ...prev, notes: e.target.value }))}
                />
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="rounded border px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" onClick={closeRegisterModal}>
                취소
              </button>
              <button
                type="button"
                className="rounded-md bg-brand-navy px-4 py-2 text-sm font-medium text-white hover:bg-brand-navy/90 disabled:opacity-50"
                onClick={saveNewContract}
                disabled={saving || registerTargetHasEffectiveConflict}
              >
                {saving ? '저장 중…' : '계약 등록'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}

function OrgContractTablePager({ totalCount, page, pageSize, totalPages, onPrev, onNext }) {
  if (totalCount <= 0) return null
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-600">
      <div>
        {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} / {totalCount}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="rounded border px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50"
          disabled={page <= 1}
          onClick={onPrev}
        >
          이전
        </button>
        <div className="min-w-[96px] text-center">
          {page} / {totalPages} 페이지
        </div>
        <button
          type="button"
          className="rounded border px-3 py-2 text-xs hover:bg-slate-50 disabled:opacity-50"
          disabled={page >= totalPages}
          onClick={onNext}
        >
          다음
        </button>
      </div>
    </div>
  )
}
